<template>
  <div class="receipt-viewer-bg min-vh-100 bg-secondary py-5 d-flex flex-column align-items-center">
    
    <div class="container d-print-none mb-4" style="max-width: 80mm;">
      <div class="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded-3">
        <button class="btn btn-outline-primary btn-sm" @click="goBack">
          <i class="bi bi-arrow-left me-1"></i> {{ $t('back') || 'Back' }}
        </button>
        <button class="btn btn-success btn-sm shadow-sm" @click="printReceipt">
          <i class="bi bi-printer me-1"></i> {{ $t('print') || 'Print' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-white my-auto d-print-none">
      <div class="spinner-border text-light mb-2" role="status"></div>
      <p>Loading Receipt Details...</p>
    </div>

    <div v-else-if="error" class="container d-print-none" style="max-width: 400px;">
      <div class="alert alert-danger text-center shadow" role="alert">
        <i class="bi bi-exclamation-triangle-fill display-5 d-block mb-2"></i>
        <h5>Error Loading Receipt</h5>
        <p class="small mb-0">{{ errorMessage }}</p>
      </div>
    </div>

    <div v-else-if="receiptData.length > 0" id="receipt-print" class="receipt-card bg-white shadow-lg p-4">
      
      <div class="text-center d-print-none mb-2">
        <span class="badge bg-light text-dark border">Receipt Preview</span>
      </div>

      <div class="receipt-header text-center">
        <img v-if="companyLogo" class="log mb-2" :src="companyLogo" crossorigin="anonymous" />
        <h3 class="fw-bold mb-1 receipt-title">{{ companyName }}</h3>
        <p class="text-muted small receipt-metadata mb-0">
          <!-- {{ $t("invoiceNumber") }} : {{ receiptId }}<br> -->
          Date: {{ receiptData[0].date }} - {{ receiptData[0].time }} 
        </p>
        <strong> {{ $t("invoiceNumber") }} : {{ receiptId }} </strong>
        <hr class="dashed-line">
      </div>

      <div v-if="receiptData[0].description" class="mt-3 p-2 bg-light border rounded text-center small receipt-note">
        <strong>Note: {{ receiptData[0].description }} </strong>
      </div>

      <table class="table table-bordered table-sm border-dark w-100 customer-info-table align-middle mb-2">
        <tbody>
          <tr>
            <td class="fw-bold bg-light py-1 border-dark" style="width: 35%;">{{ $t('customer') }}</td>
            <td class="fw-bold py-1 border-dark">{{ receiptData[0].customer_name || '-' }}</td>
          </tr>
          <tr v-if="receiptData[0].customer_mobile">
            <td class="fw-bold bg-light py-1 border-dark">{{ $t('mobile') }}</td>
            <td class="fw-bold py-1 border-dark">{{ receiptData[0].customer_mobile }}</td>
          </tr>
          <tr v-if="receiptData[0].customer_address">
            <td class="fw-bold bg-light py-1 border-dark">{{ $t('address') }}</td>
            <td class="fw-bold text-wrap py-1 border-dark">{{ receiptData[0].customer_address }}</td>
          </tr>
          <tr v-if="receiptData[0].payment">
            <td class="fw-bold bg-light py-1 border-dark"> {{ $t('payment') }}</td>
            <td class="fw-bold py-1 border-dark">{{ receiptData[0].payment }}</td>
          </tr>
          <tr v-if="receiptData[0].profite">
            <td class="fw-bold bg-light py-1 border-dark">{{ $t("type") }} {{ $t("transaction") }}</td>
            <td class="fw-bold py-1 border-dark">{{ receiptData[0].profite }}</td>
          </tr>
          <!-- <tr v-if="receiptData[0].description">
            <td class="fw-bold bg-light py-1 border-dark">{{ $t("comment") }}</td>
            <td class="text-wrap py-1 border-dark">{{ receiptData[0].description }}</td>
          </tr> -->
        </tbody>
      </table>

      <hr class="dashed-line">

      <table class="table table-bordered w-100 receipt-table align-middle">
        <thead>
          <tr>
            <th style="width: 45%;" class="text-center">{{ $t('item') }}</th>
            <th class="text-center" style="width: 15%;">{{ $t('quantity') }}</th>
            <th class="text-center" style="width: 20%;">{{ $t('price') }}</th>
            <th class="text-end" style="width: 20%;">{{ $t('total') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in receiptData" :key="item.pid">
            <td class="py-1 align-top text-start">
              <span class="fw-bold text-center d-block text-dark receipt-item-name">{{ item.product_name }}</span>
            </td>
            <td class="py-1 text-center align-top fw-bold">{{ item.quantity }}</td>
            <td class="py-1 text-center align-top">{{ Number(item.price).toFixed(2) }}</td>
            <td class="py-1 text-end align-top fw-bold">{{ Number(item.amount).toFixed(2) }}</td>
          </tr>
        </tbody>
      </table>

      <hr class="dashed-line">

      <div class="totals small">
        <div class="d-flex justify-content-between mb-1">
          <span class="text-muted">Subtotal:</span> 
          <span>{{ subtotal.toFixed(2) }} LE</span>
        </div>
        <div v-if="receiptData[0].delivery_fee" class="d-flex justify-content-between mb-1">
          <span class="text-muted">{{ $t("delivery") }}:</span> 
          <span>{{ Number(receiptData[0].delivery_fee).toFixed(2) }} LE</span>
        </div>
        <div class="d-flex justify-content-between fw-bold pt-1 border-top border-dark">
          <span>TOTAL:</span> 
          <span class="text-success print-black">{{ grandTotal.toFixed(2) }} LE</span>
        </div>
      </div>



      <hr class="dashed-line">
      <p class="text-center mt-3 mb-1 fw-bold small text-uppercase tracking-wider footer-thanks">Thank you for your visit!</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import store from '@/store';

const route = useRoute();
const router = useRouter();

// --- State Parameters ---
const receiptId = ref(route.params.id); 
const loading = ref(true);
const error = ref(false);
const errorMessage = ref('');

// CRITICAL FIX: Base value array target initialization schema setup
const receiptData = ref([]); 

// --- Corporate Profile Metadata ---
const companyLogo = ref(store.state.imgurl && store.state.cnum ? `${store.state.imgurl}/clogo/${store.state.cnum[0].cnum}.png` : '');
const companyName = ref(store.state.cnum ? store.state.cnum[0].cname : 'Our Store');

// --- Fetch Receipt Data ---
const fetchReceiptDetails = async () => {
  loading.value = true;
  error.value = false;
  try {
    const response = await axios.get(`/pos/getposreceipt/${receiptId.value}`);
    if (response.status === 200 && response.data?.data) {
      // Ensure response matches an iterable array standard structure
      receiptData.value = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
    } else {
      throw new Error("No payload found for this reference identifier.");
    }
  } catch (err) {
    console.error("Error retrieving individual receipt detail state:", err);
    error.value = true;
    errorMessage.value = err.response?.data?.message || err.message || "Failed to load receipt details.";
  } finally {
    loading.value = false;
  }
};

// --- Computed Metrics ---
const subtotal = computed(() => {
  return receiptData.value.reduce((sum, item) => sum + Number(item.amount || 0), 0);
});

const grandTotal = computed(() => {
  const delivery = receiptData.value[0]?.delivery_fee ? Number(receiptData.value[0].delivery_fee) : 0;
  return subtotal.value + delivery;
});

const printReceipt = () => {
  window.print();
};

const goBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push({ name: 'POS' });
  }
};

onMounted(() => {
  if (receiptId.value) {
    fetchReceiptDetails();
  } else {
    loading.value = false;
    error.value = true;
    errorMessage.value = "Missing receipt fallback identifier parameters.";
  }
});
</script>

<style scoped>
.receipt-viewer-bg {
  font-family: 'Courier New', Courier, monospace, system-ui;
}

/* Configures web view mode screen preview simulation sizing constraints */
.receipt-card {
  width: 80mm;
  min-height: 140mm;
  border-radius: 8px;
  box-sizing: border-box;
}

.dashed-line {
  border: none;
  border-top: 1px dashed #000 !important;
  height: 1px;
  margin: 0.5rem 0;
  opacity: 1;
}

.customer-info-table th, 
.customer-info-table td {
  border: 1px solid #000 !important;
  font-size: 0.8rem;
}

.receipt-table {
  font-size: 0.8rem;
  border-collapse: collapse;
}

.receipt-table th {
  border-bottom: 1px dashed #000;
  text-uppercase: true;
  font-size: 0.75rem;
  color: #212529;
}

.log {
  max-width: 50mm;
  height: auto;
  object-fit: contain;
}

/* --- Thermal Printer Engine Direct Pass Optimization Rules --- */
@media print {
  html, body {
    background: #fff !important;
    width: 80mm !important;
    margin: 1% !important;
    padding: 1% !important;
    left: 0 !important;
    top: 0 !important;
  }

  /* Reset alignment container */
  .receipt-viewer-bg {
    background: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    display: block !important;
    align-items: flex-start !important; /* Prevents center-flex shifting */
  }

  .receipt-card {
    box-shadow: none !important;
    padding: 2mm 0mm !important; /* Light vertical spacing, zero horizontal */
    width: 80mm !important;
    margin: 0 !important;
    float: left !important; /* Forces hardware alignment to the left edge */
  }

  /* CRITICAL: Overrides browser margin injection */
  @page {
    size: 80mm auto;
    margin: 1% !important; 
  }
}
</style>